import { datadogLogs } from '@datadog/browser-logs';
import { OxCategoriesKeys } from '@oxappsec/ox-consolidated-categories';
import { ApolloError } from '@apollo/client';
import { formatDistanceToNow } from 'date-fns';
import { GraphQLError } from 'graphql';
import { snapshot } from 'valtio';
import { AppPages, navigate } from '../../common/routing/app-navigator';
import { AppEvents } from 'common-events';
import { logger } from 'logging-utils';
import { getChosenOrganization } from '../../organizations/api/organizations-api';
import { CredentialsType } from '../connector-configure-types';
import {
  AddCredentialsInput,
  Connector,
  ConnectorsIDPErrors,
  CredentialsConfigureResponse,
  EConnectorFields,
  IdentityProviderInput,
  RemoveCredentialsInput,
} from '../connectors-types';
import connectorService from '../services';
import {
  setIsLoadingConnectors,
  updateSingleConnectorsField,
} from '../store-actions/connectors-store-actions';
import ConnectorConfigureStore from '../stores/connector-configure-store';
import ConnectorsStore from '../stores/connectors-store';
import { isTokenExpired } from '../utils/credentials-utils';
import { findConnectorById, updateConnectorFields } from './connector-actions';
import {
  openRepoSelectionModal,
  setIsLoadingDeleteCredentials,
  setIsLoadingVerifyAndSaveCredentials,
  updateSelectedConnectorField,
} from './connector-configure-actions';
import { OnboardingIDPCallbackDetail } from 'common-types';
import {
  getHubSpotFieldForSourceControl,
  submitHubSpotForm,
} from '../../hubspot-analytics/actions/hubspot-analytics-actions';
import { HubSpotFormsIds } from '../../hubspot-analytics/hubspot-types';

export const postSuccessAction = (
  credentialsConfigureResponse: CredentialsConfigureResponse,
) => {
  const { id, credentials, hostURL, isConfigured } =
    credentialsConfigureResponse;
  updateConnectorFields(id, {
    [EConnectorFields.credentials]: credentials,
    [EConnectorFields.isConfigured]: isConfigured,
    [EConnectorFields.hostURL]: hostURL,
    [EConnectorFields.errorMessage]: undefined,
    [EConnectorFields.isConnectorInvalid]: isConfigured
      ? isTokenExpired(credentials[0].tokenExpirationDate)
      : false,
  });
};

const postActionError = (
  graphQLErrors?: GraphQLError[],
  defaultErrorMessage?: string,
) => {
  const { selectedConnector } = snapshot(ConnectorConfigureStore);
  if (!selectedConnector) {
    return;
  }
  const { id: connectorId } = selectedConnector;
  const errorMsg =
    graphQLErrors?.[0]?.message ||
    defaultErrorMessage ||
    'Oops, we encountered an error';
  if (connectorId) {
    updateSingleConnectorsField(
      connectorId,
      EConnectorFields.errorMessage,
      errorMsg,
    );
    updateSelectedConnectorField(
      connectorId,
      EConnectorFields.errorMessage,
      errorMsg,
    );
  }
};

export const addCredentials = async (
  addCredentialsInput: AddCredentialsInput,
  selectedConnector: Connector,
) => {
  try {
    if (
      addCredentialsInput.hostURL !== null &&
      addCredentialsInput.hostURL.endsWith('/')
    ) {
      addCredentialsInput.hostURL = addCredentialsInput.hostURL.substring(
        0,
        addCredentialsInput.hostURL.length - 1,
      );
    }
    const result = await connectorService.addCredentials.execute(
      addCredentialsInput,
    );

    postSuccessAction(result);
    if (selectedConnector.family === OxCategoriesKeys.SourceControl) {
      const hubspotConnectorField = getHubSpotFieldForSourceControl(
        selectedConnector.displayName,
      );
      if (!hubspotConnectorField) {
        logger.warn(
          `Could not find hubspot source control field for ${selectedConnector.displayName}`,
        );
      } else {
        submitHubSpotForm(
          {
            [hubspotConnectorField]: true,
          },
          HubSpotFormsIds.OxAppForm,
        );
      }
      openRepoSelectionModal(true);
    }
  } catch (err) {
    if (err instanceof ApolloError) {
      postActionError(
        err.graphQLErrors as GraphQLError[],
        `Adding credentials failed due to an error: ${err}`,
      );
    }
  } finally {
    setIsLoadingVerifyAndSaveCredentials(false);
  }
};

export const removeCredentials = async (
  removeCredentialsInput: RemoveCredentialsInput,
): Promise<void> => {
  try {
    const result = await connectorService.removeCredentials.execute(
      removeCredentialsInput,
    );
    postSuccessAction(result);
  } catch (err) {
    if (err instanceof ApolloError) {
      postActionError(
        err.graphQLErrors as GraphQLError[],
        `Remove credentials failed due to an error: ${err}`,
      );
    }
  } finally {
    setIsLoadingDeleteCredentials(false);
  }
};

export const validateSingleConnector = async (
  connectorID: string,
  validationOnly = false,
) => {
  try {
    setSingleConnectorValidationInProgress(true);
    const results = await connectorService.validateSingleConnector.execute({
      connectorID,
    });

    if (results === null || results === undefined) {
      logger.warn(
        'validateSingleConnector: received an invalid response from validation service',
      );
      return;
    }

    if (validationOnly) {
      return results.credentialsAreValid;
    }

    const { credentialsAreValid, noReposFound } = results;
    const { connectors } = snapshot(ConnectorsStore);
    const connectorToUpdate = connectors.find(
      connector => connector.id === connectorID,
    );
    if (!connectorToUpdate) {
      logger.error(
        'Credentials Validation failed: could not find a connector with id: ',
        connectorID,
      );
      return;
    }

    // For now supporting only single credentials
    const creds = { ...connectorToUpdate.credentials[0] };
    if (!credentialsAreValid) {
      creds.tokenExpirationDate = new Date().toUTCString();
    } else {
      creds.tokenExpirationDate = undefined;
    }

    updateConnectorFields(connectorID, {
      [EConnectorFields.credentials]: [creds],
      [EConnectorFields.isEmptyOfRepos]: noReposFound,
      [EConnectorFields.isConnectorInvalid]: isTokenExpired(
        creds.tokenExpirationDate,
      ),
    });

    showSingleValidationMsg(credentialsAreValid, noReposFound);
  } catch (err) {
    logger.error(err);
    showValidationError();
  } finally {
    setSingleConnectorValidationInProgress(false);
  }
};

export const validateAllCredentials = async (): Promise<void> => {
  setCredentialsValidationInProgress(true);

  try {
    const chosenOrg = getChosenOrganization();
    const orgId = chosenOrg?.id;
    if (!orgId) {
      logger.warn(
        'Validating all credentials did not execute because of a missing org id in the chosen organization: ',
        chosenOrg,
      );
      return;
    }
    const results = await connectorService.validateAllCredentials.execute(
      orgId,
    );

    if (results === null || results === undefined) {
      return;
    }
    if (results.invalidCredentialsConnectors.length > 0) {
      setIsLoadingConnectors(true);
      const { connectors } = snapshot(ConnectorsStore);
      results.invalidCredentialsConnectors.forEach(connectorId => {
        const connectorToUpdate = connectors.find(
          connector => connector.id === connectorId,
        );
        if (!connectorToUpdate) {
          logger.error(
            'Credentials Validation: could not find a connector with id: ',
            connectorId,
          );
          return;
        }
        // For now supporting only single credentials
        const creds = { ...connectorToUpdate.credentials[0] };
        creds.tokenExpirationDate = new Date().toUTCString();
        updateConnectorFields(connectorId, {
          [EConnectorFields.credentials]: [creds],
          [EConnectorFields.isConnectorInvalid]: true,
        });
      });
      setIsLoadingConnectors(false);
    }
    updateValidConnectors(results.invalidCredentialsConnectors);
    showValidationMsg();
  } catch (err) {
    logger.error(err);
    showValidationError();
  } finally {
    setCredentialsValidationInProgress(false);
  }
};

const updateValidConnectors = (invalidConnectorsIdList: string[]) => {
  const { connectors } = snapshot(ConnectorsStore);
  const invalidConnectors = connectors.filter(
    connector => !!connector.credentials[0]?.tokenExpirationDate,
  );
  invalidConnectors.forEach(connector => {
    if (!invalidConnectorsIdList.includes(connector.id)) {
      const creds = { ...connector.credentials[0] };
      creds.tokenExpirationDate = undefined;
      updateConnectorFields(connector.id, {
        [EConnectorFields.credentials]: [creds],
        [EConnectorFields.isConnectorInvalid]: false,
      });
    }
  });
};

export const showValidationMsg = () => {
  ConnectorsStore.showValidationMsg = true;
};

export const dismissValidationMsg = () => {
  ConnectorsStore.showValidationMsg = false;
};

export const showValidationError = () => {
  ConnectorsStore.validationError = true;
};

export const dismissValidationError = () => {
  ConnectorsStore.validationError = false;
};

export const showSingleValidationMsg = (
  isConnectorValid: boolean,
  noReposFound: boolean,
) => {
  ConnectorsStore.showSingleValidationMsg = true;
  ConnectorsStore.singleConnectorIsEmptyOfRepos = noReposFound;
  ConnectorsStore.singleConnectorIsValid = isConnectorValid;
};

export const dismissSingleValidationMsg = () => {
  ConnectorsStore.showSingleValidationMsg = false;
  ConnectorsStore.singleConnectorIsEmptyOfRepos = undefined;
  ConnectorsStore.singleConnectorIsValid = undefined;
};

export const setCredentialsValidationInProgress = (isInProgress: boolean) => {
  ConnectorsStore.credentialsValidationInProgress = isInProgress;
};

export const setSingleConnectorValidationInProgress = (
  isInProgress: boolean,
) => {
  ConnectorsStore.singleConnectorValidationInProgress = isInProgress;
};

export const createTokenExpirationHelperMsg = (connector: Connector) => {
  const {
    singleConnectorIsValid,
    showSingleValidationMsg,
    singleConnectorIsEmptyOfRepos,
  } = snapshot(ConnectorsStore);
  if (
    showSingleValidationMsg &&
    singleConnectorIsValid &&
    !singleConnectorIsEmptyOfRepos
  ) {
    return 'Connectivity Verified!';
  }
  if (
    (connector.credentials.length === 0 ||
      !connector.credentials[0].tokenExpirationDate) &&
    !connector.isEmptyOfRepos
  ) {
    return '';
  }

  if (
    connector.isEmptyOfRepos &&
    !connector.credentials[0]?.tokenExpirationDate
  ) {
    return 'Could not fetch your repositories, please check the provided token scopes';
  }

  if (!connector.credentials[0].tokenExpirationDate) {
    return '';
  }

  const expDate = new Date(connector.credentials[0].tokenExpirationDate);
  if (expDate.toString() === 'Invalid Date') {
    return '';
  }
  if (isTokenExpired(expDate)) {
    return `Your token has become invalid. Please add a new token.`;
  }
  return `Your token will expire ${formatDistanceToNow(expDate, {
    addSuffix: true,
  })}`;
};

export const configureIDP = async (
  idpCode: string,
  idpState: string,
): Promise<{ success: boolean; error?: string }> => {
  try {
    const decodedState = decodeURIComponent(idpState);
    const parsedState = JSON.parse(decodedState);

    const { idpBaseURL, exchangeAuthorizationCodeURL } = parsedState;

    const idpInput: IdentityProviderInput = {
      code: idpCode,
      state: idpState,
      identityProviderBaseURL: exchangeAuthorizationCodeURL || idpBaseURL,
    };

    const result = await connectorService.configureIdentityProvider.execute(
      idpInput,
    );
    if (!result) {
      return {
        success: false,
        error:
          'Oops.. An unknown error happened, please refresh the page and try again',
      };
    }
    postSuccessAction(result);
    return { success: true };
  } catch (err) {
    let errMsg = `Didn't manage to add credentials due to an error: ${err}`;
    if (err instanceof ApolloError) {
      postActionError(err.graphQLErrors as GraphQLError[], errMsg);
      errMsg = err.graphQLErrors[0].message || errMsg;
    }
    return { success: false, error: errMsg };
  } finally {
    setIsLoadingVerifyAndSaveCredentials(false);
  }
};

export const checkWhichTabToShow = () => {
  const { selectedConnector } = snapshot(ConnectorConfigureStore);
  if (!selectedConnector) {
    return null;
  }
  if (
    selectedConnector.isConfigured &&
    selectedConnector.credentials[0].credentialsType
  ) {
    return CredentialsType[selectedConnector.credentials[0].credentialsType];
  }

  return null;
};

export const handleIdpCallback = async (searchParams: URLSearchParams) => {
  const idpCode = searchParams.get('code');
  const idpState = searchParams.get('state');
  const error = searchParams.get('error');

  if (!idpState) {
    navigate(
      AppPages.Connectors,
      `idpError=${
        error === 'access_denied'
          ? ConnectorsIDPErrors.AccessDenied
          : ConnectorsIDPErrors.MissingState
      }`,
    );
    return;
  }
  const decodedState = decodeURIComponent(idpState);
  const parsedState = JSON.parse(decodedState);
  if (error) {
    logger.error(error);
    const errType =
      error === 'access_denied'
        ? ConnectorsIDPErrors.AccessDenied
        : ConnectorsIDPErrors.GeneralError;
    navigate(
      AppPages.Connectors,
      `connectorId=${parsedState.connectorId}&idpError=${errType}`,
    );
    return;
  }
  if (!idpCode) {
    navigate(
      AppPages.Connectors,
      `connectorId=${parsedState.connectorId}&idpError=${ConnectorsIDPErrors.MissingCode}`,
    );
    return;
  }
  const configureResults = await configureIDP(idpCode, idpState);
  if (parsedState.onboarding) {
    datadogLogs.logger.info(
      `Onboarding: ${
        configureResults.success
          ? 'Connected successfully'
          : 'Failed connecting'
      } using IDP to connector ID ${parsedState.connectorId}`,
      { connectorId: parsedState.connectorId },
    );
    document.dispatchEvent(
      new CustomEvent<OnboardingIDPCallbackDetail>(
        configureResults.success
          ? AppEvents.Onboarding.ConnectIdpSuccess
          : AppEvents.Onboarding.ConnectIdpFailed,
        {
          detail: {
            connectorId: parsedState.connectorId,
            error: configureResults.error,
          },
        },
      ),
    );
  } else {
    const connector = findConnectorById(parsedState.connectorId);
    if (configureResults.success) {
      const sourceControlHubSpotField = getHubSpotFieldForSourceControl(
        connector?.displayName || '',
      );
      if (!sourceControlHubSpotField) {
        logger.warn(
          `Could not find HubSpot source control field for ${connector?.displayName} with id ${parsedState.connectorId}`,
        );
      } else {
        submitHubSpotForm(
          {
            [sourceControlHubSpotField]: true,
          },
          HubSpotFormsIds.OxAppForm,
        );
      }
    }
    navigate(
      AppPages.Connectors,
      `connectorId=${parsedState.connectorId}&idp=true${
        configureResults.success
          ? ''
          : `&idpError=${
              configureResults.error || ConnectorsIDPErrors.ValidationError
            }`
      }`,
    );
  }
};
